import { z } from 'zod';
import { AllCharLabelSchema, HttpUrlSchema, SubjectSchema } from '../common/schemas';
import {
  DBNotesCategoryCategorySchema,
  NotesCategoryCodeSchema,
  NotesCategoryIdSchema,
} from '../notesCategories/schemas';

/**
 * Shared & Helper Schemas
 */

/**
 * DB Schemas
 */

export const DBNoteSubjectSchema = SubjectSchema;
export const DBNoteNoteIdSchema = z
  .string()
  .refine(val => {
    const parts = val.split('#');

    return (
      parts.length === 3 &&
      z.lazy(() => DBNoteSubjectSchema).safeParse(parts[0]).success &&
      z.lazy(() => DBNotesCategoryCategorySchema).safeParse(parts[1]).success &&
      z.lazy(() => DBNoteKeySchema).safeParse(parts[2]).success
    );
  })
  .openapi({
    description: 'Unique note identifier in format: subject#category#key',
    example: 'chem#mod1#les1',
  });
export const DBNoteCategorySchema = z
  .lazy(() => DBNotesCategoryCategorySchema)
  .openapi({
    description: 'Category key for note organization (4 alphanumeric characters)',
    example: 'mod1',
  });
export const DBNoteHrefSchema = HttpUrlSchema.openapi({
  description: 'URL or reference link for the note (1-2480 characters)',
  example: 'https://cognito-online-notes.s3.amazonaws.com/chem#mod1#les1',
});
export const DBNoteIsExternalLinkSchema = z.boolean().openapi({
  description: 'Whether the note href points to an external link',
  example: true,
});
// Matches DBLessonTemplateLessonIdSchema: a note is keyed by the lesson whose homework
// it solves, and a lesson code is not always four characters (e.g. F1L10)
export const DBNoteKeySchema = z
  .string()
  .regex(/^[a-zA-Z0-9]{1,8}$/)
  .openapi({
    description: 'Unique key for the note within its category (1-8 alphanumeric characters)',
    example: 'les1',
  });

export const DBNoteLabelSchema = AllCharLabelSchema.min(1).max(64).openapi({
  description: 'Display label for the note (1-64 characters)',
  example: 'Lesson 1: Nomenclature',
});

export const DBNoteShortLabelSchema = AllCharLabelSchema.min(1).max(16).openapi({
  description: 'Short display label for the note (1-16 characters)',
  example: 'L1 Nomenclature',
});

export const DBNoteSchema = z
  .object({
    subject: DBNoteSubjectSchema,
    noteId: DBNoteNoteIdSchema,
    category: DBNoteCategorySchema,
    href: DBNoteHrefSchema,
    isExternalLink: DBNoteIsExternalLinkSchema,
    key: DBNoteKeySchema,
    label: DBNoteLabelSchema,
    shortLabel: DBNoteShortLabelSchema,
  })
  .openapi({
    description: 'Database schema for individual notes',
    example: {
      subject: 'chem',
      noteId: 'chem#mod1#les1',
      category: 'mod1',
      href: 'https://cognito-online-notes.s3.amazonaws.com/chem#mod1#les1',
      isExternalLink: true,
      key: 'les1',
      label: 'Lesson 1: Nomenclature',
      shortLabel: 'L1 Nomenclature',
    },
  });

/**
 * Client Schemas
 */

export const NoteIdSchema = z.string().brand<'NoteId'>();
export const NoteSubjectSchema = DBNoteSubjectSchema;
export const NoteCategoryCodeSchema = z.lazy(() => NotesCategoryCodeSchema);
export const NoteCategoryIdSchema = z.lazy(() => NotesCategoryIdSchema);
export const NoteHrefSchema = DBNoteHrefSchema;
export const NoteIsExternalLinkSchema = DBNoteIsExternalLinkSchema;
export const NoteCodeSchema = DBNoteKeySchema;
export const NoteLabelSchema = DBNoteLabelSchema;
export const NoteShortLabelSchema = DBNoteShortLabelSchema;

export const NoteSchema = z
  .object({
    id: NoteIdSchema,
    subject: NoteSubjectSchema,
    categoryCode: NoteCategoryCodeSchema,
    categoryId: NoteCategoryIdSchema,
    href: NoteHrefSchema,
    isExternalLink: NoteIsExternalLinkSchema.default(false),
    code: NoteCodeSchema,
    label: NoteLabelSchema,
    shortLabel: NoteShortLabelSchema,
  })
  .openapi({
    description: 'Client-side schema for individual notes',
    example: {
      // ["note","chem","mod1","les1"] and ["notesCategory","chem","mod1"], base64url-encoded
      id: 'WyJub3RlIiwiY2hlbSIsIm1vZDEiLCJsZXMxIl0',
      subject: 'chem',
      categoryCode: 'mod1',
      categoryId: 'WyJub3Rlc0NhdGVnb3J5IiwiY2hlbSIsIm1vZDEiXQ',
      href: 'https://cognito-online-notes.s3.amazonaws.com/chem#mod1#les1',
      isExternalLink: true,
      code: 'les1',
      label: 'Lesson 1: Nomenclature',
      shortLabel: 'L1 Nomenclature',
    },
  });
